import React, { useState, useEffect } from 'react';
import { XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline';

interface FullscreenCameraProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onUploadInstead: () => void;
  isMobile: boolean;
}

const FullscreenCamera: React.FC<FullscreenCameraProps> = ({
   children,
   isOpen,
   onClose,
   onUploadInstead,
   isMobile
 }) => {
  const [isClosing, setIsClosing] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isMobile]);

  const handleClose = (): void => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  if (!isOpen) return null;

  // If not mobile, just render children normally
  if (!isMobile) {
    return (
      <div className="space-y-4 max-w-2xl mx-auto">
        {children}
        <div className="flex gap-2 items-center justify-center">
          <button
            onClick={onUploadInstead}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PhotoIcon className="h-4 w-4 mr-2" />
            Upload Instead
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-0 z-50 bg-black flex flex-col transition-opacity duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      style={{
        // Account for mobile browser UI
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      {/* Header */}
      <div className='flex justify-between items-center h-12 px-4 bg-black/50 backdrop-blur-sm'>
        <button
          onClick={handleClose}
          className='p-2 rounded-full text-white hover:bg-white/10'
        >
          <XMarkIcon className='h-6 w-6' />
        </button>
      </div>

      {/* Camera Content */}
      <div className="flex-1 relative">
        {children}
      </div>

      <div className='h-16' />
    </div>
  );
};

export default FullscreenCamera;